import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedLeaderboard = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const limit = parseInt(searchParams.get('limit')) || 10;
  const showHeader = searchParams.get('header') !== 'false';
  const showPnL = searchParams.get('pnl') !== 'false';
  const showRank = searchParams.get('rank') !== 'false';
  const style = searchParams.get('style') || 'compact'; // compact, full, minimal
  const accentColor = searchParams.get('accent') || '#ffd700';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const animate = searchParams.get('animate') !== 'false';

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const prevRanksRef = useRef({});

  // Fetch initial leaderboard
  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/leaderboard?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        updateLeaderboard(data.slice(0, limit));
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeaderboard = (newData) => {
    // Track rank changes for animation
    const currentRanks = {};
    newData.forEach((user, index) => {
      const prevRank = prevRanksRef.current[user.id];
      currentRanks[user.id] = index + 1;
      user.rankChange = prevRank ? prevRank - (index + 1) : 0;
    });
    prevRanksRef.current = currentRanks;
    setLeaderboard(newData);
  };

  useEffect(() => {
    fetchLeaderboard();

    // Set up socket for real-time updates
    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('leaderboard-update', (data) => {
      const list = Array.isArray(data) ? data : data?.leaderboard;
      if (Array.isArray(list)) {
        updateLeaderboard(list.slice(0, limit));
      }
    });

    // Fallback polling
    const interval = setInterval(fetchLeaderboard, refreshInterval);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(interval);
    };
  }, [limit, refreshInterval]);

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const formatPnL = (user) => {
    const pnl = user.totalValue - 10000; // Assuming 10k starting balance
    const pnlPercent = user.percentGain || 0;
    return {
      value: pnl,
      percent: pnlPercent,
      isPositive: pnl >= 0
    };
  };

  if (loading) {
    return (
      <div className="embed-container embed-leaderboard" style={{ background: bg }}>
        <div className="embed-loading">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div
      className={`embed-container embed-leaderboard ${theme} style-${style}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && (
        <div className="leaderboard-header" style={{ borderColor: accentColor }}>
          <span className="leaderboard-title">🏆 LEADERBOARD</span>
          <span className="leaderboard-count">{leaderboard.length} traders</span>
        </div>
      )}

      <div className="leaderboard-list">
        {leaderboard.length === 0 && (
          <div className="embed-waiting">Waiting for traders...</div>
        )}
        {leaderboard.map((user, index) => {
          const rank = index + 1;
          const pnl = showPnL ? formatPnL(user) : null;
          const isTop3 = rank <= 3;

          return (
            <div
              key={user.id}
              className={`leaderboard-item ${isTop3 ? 'top-three' : ''} ${animate && user.rankChange !== 0 ? 'rank-changed' : ''}`}
              style={{
                '--rank-change': user.rankChange,
                '--accent': accentColor,
              }}
            >
              {showRank && (
                <div className={`item-rank ${isTop3 ? `rank-${rank}` : ''}`}>
                  {isTop3 ? getRankBadge(rank) : <span className="rank-number">{rank}</span>}
                </div>
              )}

              <div className="item-info">
                <span className="item-name">
                  {user.username || user.shortId || `Trader ${user.id?.slice(-4)}`}
                </span>
                {style === 'full' && (
                  <span className="item-id">{user.shortId || user.id?.slice(0, 8)}</span>
                )}
              </div>

              <div className="item-value">
                <span className="item-portfolio">{formatCurrency(user.totalValue)}</span>
                {showPnL && pnl && (
                  <span className={`item-pnl ${pnl.isPositive ? 'positive' : 'negative'}`}>
                    {pnl.isPositive ? '+' : ''}{formatCurrency(pnl.value)}
                    <span className="pnl-percent">({pnl.isPositive ? '+' : ''}{pnl.percent.toFixed(1)}%)</span>
                  </span>
                )}
              </div>

              {animate && user.rankChange !== 0 && (
                <div className={`rank-change-indicator ${user.rankChange > 0 ? 'up' : 'down'}`}>
                  {user.rankChange > 0 ? `↑${user.rankChange}` : `↓${Math.abs(user.rankChange)}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmbedLeaderboard;
